import { ACCESS_TOKEN_QUERY_PARAM } from '../hooks/useAccessToken';

export type AccessGrantTarget =
  | { objectType: 'entity'; objectId: string | number }
  | { objectType: 'initiative'; objectId: string | number };

/**
 * Build the canonical share path for a target object. This intentionally
 * lives outside of the API package so the FE can compose URLs entirely from
 * a token + a target without an extra round-trip.
 */
export function buildAccessGrantSharePath(target: AccessGrantTarget): string {
  switch (target.objectType) {
    case 'initiative':
      return `/initiatives/${target.objectId}`;
    case 'entity':
      return `/entities/${target.objectId}`;
  }
}

/**
 * Compose a grant link given a raw token and target. Returns an absolute URL
 * when running in the browser, or a relative one in SSR contexts.
 *
 * Note: the API also returns a server-built `shareUrl` for each grant. Use
 * this helper when you need to override the target (e.g., an entity-scoped
 * grant being applied to a specific initiative).
 */
export function buildAccessGrantUrl(args: { token: string; target: AccessGrantTarget }): string {
  const path = buildAccessGrantSharePath(args.target);
  const separator = path.includes('?') ? '&' : '?';
  const relative = `${path}${separator}${ACCESS_TOKEN_QUERY_PARAM}=${encodeURIComponent(args.token)}`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${relative}`;
  }
  return relative;
}
